import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, Menu, X } from "lucide-react";

const LOGO_URL =
    "https://customer-assets-wrfwihn1.emergentagent.net/job_authority-scripts/artifacts/p0dveaq9_UnCloned%C2%AE%20Logo%20with%20Registered%20R%20%28Various%20Colors%29-01.webp";

export const Header = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    const isAdmin = user?.role === "admin";
    const isClient = user?.role === "client";

    const doLogout = () => {
        setMobileOpen(false);
        logout();
        navigate("/login");
    };

    const active = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

    // Close mobile menu whenever route changes
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const clientLinks = [
        { to: "/dashboard", label: "Projects", testid: "nav-dashboard", isActive: active("/dashboard") },
        { to: "/profile", label: "My Profile", testid: "nav-profile", isActive: active("/profile") },
    ];
    const adminLinks = [
        { to: "/admin", label: "Overview", testid: "nav-admin-overview", isActive: location.pathname === "/admin" },
        { to: "/admin/clients", label: "Clients", testid: "nav-admin-clients", isActive: active("/admin/clients") },
        { to: "/admin/codes", label: "Codes", testid: "nav-admin-codes", isActive: active("/admin/codes") },
        { to: "/admin/tags", label: "Tags", testid: "nav-admin-tags", isActive: active("/admin/tags") },
        { to: "/admin/prompt", label: "Prompt", testid: "nav-admin-prompt", isActive: active("/admin/prompt") },
    ];
    const navLinks = isAdmin ? adminLinks : isClient ? clientLinks : [];

    return (
        <header className="border-b-2 border-un-onyx bg-white sticky top-0 z-30" data-testid="app-header">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-4 flex items-center justify-between gap-4">
                <Link
                    to={user ? (isAdmin ? "/admin" : "/dashboard") : "/"}
                    className="flex items-center gap-3 shrink-0"
                    data-testid="brand-home-link"
                >
                    <img
                        src={LOGO_URL}
                        alt="UnCloned"
                        className="h-6 sm:h-7 w-auto select-none"
                        draggable={false}
                    />
                </Link>

                {/* Desktop nav (md and up) */}
                {user && (
                    <nav className="hidden md:flex items-center gap-1 md:gap-2" data-testid="primary-nav">
                        {navLinks.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                className={`un-btn-ghost !py-2 !px-3 text-sm ${l.isActive ? "text-un-berry" : ""}`}
                                data-testid={l.testid}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <div className="flex items-center gap-2 pl-4 ml-2 border-l-2 border-un-onyx">
                            <div className="text-right leading-tight">
                                <div className="un-mono text-xs text-un-onyx/60">{user.email}</div>
                                <div className="un-label text-un-grape">{isAdmin ? "Admin" : "Client"}</div>
                            </div>
                        </div>
                        <button
                            onClick={doLogout}
                            className="un-btn-outline !py-2 !px-3 ml-2 flex items-center gap-2 text-sm"
                            data-testid="logout-button"
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Logout</span>
                        </button>
                    </nav>
                )}

                {/* Mobile hamburger toggle */}
                {user && (
                    <button
                        type="button"
                        onClick={() => setMobileOpen((v) => !v)}
                        className="md:hidden un-btn-outline !p-2 flex items-center justify-center"
                        aria-label={mobileOpen ? "Close menu" : "Open menu"}
                        aria-expanded={mobileOpen}
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                )}

                {!user && (
                    <div className="flex items-center gap-1.5 md:gap-2">
                        <Link to="/login" className="un-btn-ghost !text-xs md:!text-sm !px-2 md:!px-3" data-testid="nav-login">
                            Login
                        </Link>
                        <Link
                            to="/signup"
                            className="un-btn-primary !text-xs md:!text-sm !px-3 md:!px-5 !py-2 md:!py-3"
                            data-testid="nav-signup"
                        >
                            Redeem Code
                        </Link>
                    </div>
                )}
            </div>

            {/* Mobile menu panel */}
            {user && mobileOpen && (
                <div
                    className="md:hidden border-t-2 border-un-onyx bg-white"
                    data-testid="mobile-menu-panel"
                >
                    <div className="px-4 py-4 flex flex-col gap-1">
                        {navLinks.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                className={`un-btn-ghost !py-3 !px-3 text-base text-left ${l.isActive ? "text-un-berry" : ""}`}
                                data-testid={`${l.testid}-mobile`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <div className="border-t-2 border-un-onyx mt-3 pt-3 px-3 flex items-center justify-between gap-3">
                            <div className="leading-tight min-w-0">
                                <div className="un-mono text-xs text-un-onyx/60 truncate">{user.email}</div>
                                <div className="un-label text-un-grape">{isAdmin ? "Admin" : "Client"}</div>
                            </div>
                            <button
                                onClick={doLogout}
                                className="un-btn-outline !py-2 !px-3 flex items-center gap-2 text-sm shrink-0"
                                data-testid="logout-button-mobile"
                            >
                                <LogOut className="w-4 h-4" />
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};
